import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle, Clock, Users, BookOpen, TrendingUp, Plus } from 'lucide-react';
import { calculateDaysUntilExpiration, formatDate } from '../utils/dataModels';
import ScreenReaderContent from './ScreenReaderContent';

const Dashboard = ({ students, goals, onNavigate, announce }) => {
  const alertData = useMemo(() => {
    const alerts = students.map(student => {
      const daysUntil = calculateDaysUntilExpiration(student.iepEndDate);
      let alertLevel = 'info';
      
      if (daysUntil <= 30) alertLevel = 'critical';
      else if (daysUntil <= 60) alertLevel = 'warning';
      
      return {
        student,
        daysUntil,
        alertLevel
      };
    }).filter(alert => alert.daysUntil <= 90);
    
    return alerts.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [students]);

  const statistics = useMemo(() => {
    const totalStudents = students.length;
    const totalGoals = goals.length;
    const completedGoals = goals.filter(goal => goal.status === 'Mastered').length;
    const inProgressGoals = goals.filter(goal => goal.status === 'In Progress').length;
    
    return {
      totalStudents,
      totalGoals,
      completedGoals,
      inProgressGoals,
      completionRate: totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0
    };
  }, [students, goals]);

  const AlertCard = ({ student, daysUntil, alertLevel }) => {
    const urgencyText = alertLevel === 'critical' ? 'Urgent' : alertLevel === 'warning' ? 'Important' : 'Upcoming';
    
    const alertStyles = {
      critical: 'border-red-500 bg-red-50 text-red-800',
      warning: 'border-yellow-500 bg-yellow-50 text-yellow-800',
      info: 'border-green-500 bg-green-50 text-green-800'
    };

    return (
      <div 
        className={`p-4 rounded-lg border-l-4 ${alertStyles[alertLevel]} transition-colors hover:bg-opacity-70`}
        role="alert"
        aria-labelledby={`alert-${student.id}-title`}
        aria-describedby={`alert-${student.id}-desc`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 id={`alert-${student.id}-title`} className="font-semibold flex items-center">
              <ScreenReaderContent>{urgencyText}: </ScreenReaderContent>
              <AlertTriangle size={16} className="mr-2" />
              {student.firstName} {student.lastName}
            </h3>
            <p id={`alert-${student.id}-desc`} className="mt-1 text-sm">
              IEP expires in {daysUntil} days on {formatDate(student.iepEndDate)}
            </p>
          </div>
          <button
            onClick={() => onNavigate('students', { studentId: student.id })}
            className="ml-4 px-3 py-1 text-xs font-medium bg-white border border-current rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
            aria-label={`View details for ${student.firstName} ${student.lastName}`}
          >
            View
          </button>
        </div>
      </div>
    );
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, onClick, color = 'blue' }) => {
    const colorStyles = {
      blue: 'bg-blue-50 text-blue-700',
      green: 'bg-green-50 text-green-700',
      yellow: 'bg-yellow-50 text-yellow-700',
      purple: 'bg-purple-50 text-purple-700'
    };

    const cardContent = (
      <div className={`p-6 rounded-lg border ${colorStyles[color]} transition-colors ${onClick ? 'hover:bg-opacity-70 cursor-pointer' : ''} h-full`}>
        <div className="flex items-center h-full">
          <div className="flex-shrink-0">
            <Icon size={24} />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-sm opacity-80">{subtitle}</p>}
          </div>
        </div>
      </div>
    );

    return onClick ? (
      <button 
        onClick={onClick}
        className="w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        aria-label={`${title}: ${value}. ${subtitle || ''} Click to view details.`}
      >
        {cardContent}
      </button>
    ) : (
      <div aria-label={`${title}: ${value}. ${subtitle || ''}`}>
        {cardContent}
      </div>
    );
  };

  const QuickActionButton = ({ label, onClick, icon: Icon, color = 'primary' }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        color === 'primary' 
          ? 'bg-primary text-white hover:bg-blue-700 focus:ring-blue-500'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'
      }`}
    >
      <Icon size={20} className="mr-2" />
      {label}
    </button>
  );

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-900 mt-2">Overview of your IEP management activities</p>
      </div>

      {/* Quick Actions */}
      <section aria-labelledby="quick-actions-heading">
        <h2 id="quick-actions-heading" className="text-xl font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickActionButton
            label="Add Student"
            icon={Plus}
            onClick={() => onNavigate('students', { action: 'add' })}
          />
          <QuickActionButton
            label="Enter Progress"
            icon={TrendingUp}
            onClick={() => onNavigate('progress')}
          />
          <QuickActionButton
            label="Create Goal"
            icon={BookOpen}
            onClick={() => onNavigate('goals', { action: 'add' })}
          />
        </div>
      </section>

      {/* Statistics */}
      <section aria-labelledby="statistics-heading">
        <h2 id="statistics-heading" className="text-xl font-semibold text-gray-900 mb-4">
          Overview Statistics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Students"
            value={statistics.totalStudents}
            icon={Users}
            onClick={() => onNavigate('students')}
            color="blue"
          />
          <StatCard
            title="Total Goals"
            value={statistics.totalGoals}
            icon={BookOpen}
            onClick={() => onNavigate('goals')}
            color="purple"
          />
          <StatCard
            title="Completed Goals"
            value={statistics.completedGoals}
            subtitle={`${statistics.completionRate}% completion rate`}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="In Progress"
            value={statistics.inProgressGoals}
            icon={Clock}
            color="yellow"
          />
        </div>
      </section>

      {/* IEP Expiration Alerts */}
      <section aria-labelledby="alerts-heading">
        <h2 id="alerts-heading" className="text-xl font-semibold text-gray-900 mb-4">
          IEP Expiration Alerts
          <ScreenReaderContent>
            {alertData.length === 0 ? 'No alerts' : `${alertData.length} alerts`}
          </ScreenReaderContent>
        </h2>
        
        {alertData.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">All IEPs are current</h3>
            <p className="text-gray-600">No upcoming expiration dates within 90 days</p>
          </div>
        ) : (
          <div className="space-y-4">
            {alertData.map(({ student, daysUntil, alertLevel }) => (
              <AlertCard
                key={student.id}
                student={student}
                daysUntil={daysUntil}
                alertLevel={alertLevel}
              />
            ))}
          </div>
        )}
      </section>

      {/* Recent Activity */}
      <section aria-labelledby="recent-activity-heading">
        <h2 id="recent-activity-heading" className="text-xl font-semibold text-gray-900 mb-4">
          Recent Activity
        </h2>
        <div className="bg-white rounded-lg border p-6">
          <p className="text-gray-900 text-center py-4">
            Activity feed will be populated as you use the application
          </p>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;